import { Shield, Heart, Check, Award, TreePine, Phone, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <section className="bg-primary py-16 lg:py-24" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-primary-foreground mb-6">
                About Critter Whisperer Charlie
              </h1>
              <p className="text-primary-foreground/80 font-body text-lg">
                Patrick Enterprises Canada has been serving Eastern Ontario communities with professional 
                wildlife control services. We are committed to humane practices that protect both your home 
                and local wildlife.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1762180402317-96245510398f?w=600"
                alt="Forest cabin in Ontario"
                className="w-full rounded-sm border-2 border-primary-foreground/20"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4" data-testid="our-story">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-8 text-center">
            Our Story
          </h2>
          <div className="prose prose-lg max-w-none font-body text-muted-foreground">
            <p className="mb-6">
              At Patrick Enterprises Canada, we understand the delicate balance between human habitation 
              and wildlife. Living in beautiful Eastern Ontario means sharing our environment with 
              incredible creatures - but sometimes, that sharing goes a little too far when animals 
              decide to make your attic their home.
            </p>
            <p className="mb-6">
              Our approach is simple: <strong className="text-foreground">Prevent house fires. Catch and patch.</strong> 
              This slogan reflects our comprehensive method. Animals like squirrels and raccoons can cause 
              significant fire hazards by chewing through electrical wiring. We do not just remove the 
              animals - we seal entry points and repair damage to protect your home for the long term.
            </p>
            <p>
              We take pride in using <strong className="text-foreground">humane live trapping methods</strong>. 
              Every animal we encounter is treated with respect and relocated safely. We believe in 
              solving wildlife conflicts in a way that is fair to both homeowners and the animals themselves.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary/5" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-12 text-center">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm text-center" data-testid="value-humane">
              <Heart className="h-12 w-12 text-accent mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg uppercase mb-2">Humane Treatment</h3>
              <p className="text-muted-foreground font-body text-sm">
                Every animal is treated with care and respect using live trapping methods.
              </p>
            </div>
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm text-center" data-testid="value-professional">
              <Shield className="h-12 w-12 text-accent mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg uppercase mb-2">Professional Service</h3>
              <p className="text-muted-foreground font-body text-sm">
                Fully licensed and insured for your complete peace of mind.
              </p>
            </div>
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm text-center" data-testid="value-thorough">
              <Award className="h-12 w-12 text-accent mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg uppercase mb-2">Thorough Solutions</h3>
              <p className="text-muted-foreground font-body text-sm">
                We do not just remove - we prevent future problems with complete exclusion.
              </p>
            </div>
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm text-center" data-testid="value-local">
              <TreePine className="h-12 w-12 text-accent mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg uppercase mb-2">Local Expertise</h3>
              <p className="text-muted-foreground font-body text-sm">
                Deep knowledge of Ontario wildlife and effective local solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-8">
                Why Choose Us?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1">Fully Licensed and Insured</h3>
                    <p className="text-muted-foreground font-body">
                      We carry full liability insurance and all required wildlife control certifications.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1">Humane Live Trapping</h3>
                    <p className="text-muted-foreground font-body">
                      No poisons, no harm. We relocate animals safely and humanely.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1">Complete Solutions</h3>
                    <p className="text-muted-foreground font-body">
                      From removal to exclusion to cleanup - we handle everything.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1">Fast Response</h3>
                    <p className="text-muted-foreground font-body">
                      We understand wildlife emergencies and respond promptly to calls.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1684936354722-3cf32fe7496f?w=400"
                alt="Raccoon in tree"
                className="rounded-sm border-2 border-secondary/20"
              />
              <img
                src="https://images.unsplash.com/photo-1688825929047-a420e35ffa81?w=400"
                alt="Squirrel"
                className="rounded-sm border-2 border-secondary/20 mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary-foreground mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-primary-foreground/80 font-body text-lg mb-8">
            Contact us today for a free consultation and quote. We are here to help protect your home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
              data-testid="about-cta-contact"
            >
              Contact Us
              <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:343-263-2000"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-foreground text-primary-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide transition-all hover:bg-primary-foreground hover:text-primary"
              data-testid="about-cta-call"
            >
              <Phone className="h-4 w-4" />
              343-263-2000
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
